import { Outlet, Navigate, useLocation, Link } from 'react-router-dom'
import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '@/contexts/auth-context'
import { Sidebar } from './Sidebar'
import { Loader2, AlignLeft, ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { isElectron, type MediaFile } from '@/lib/electron'
import { track } from '@/lib/track'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { DesktopAppBanner } from './DesktopAppBanner'
import { DropZone } from '@/components/DropZone'
import { UpdateGate } from '@/components/UpdateGate'
import { useMutation, useQuery } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import { toast } from '@/hooks/use-toast'
import { CommunityPanel } from '@/components/community/CommunityPanel'
import { useFF } from '@/platform/feature-flags/useFeatureFlag'
import { AppModalsProvider } from '@/contexts/app-modals-context'
import { SUPPORT_EMAIL } from '@/lib/config/site'

export function AppLayout() {
  const { isAuthenticated, isLoading, user } = useAuth()
  const location = useLocation()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [communityOpen, setCommunityOpen] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    return params.has('msg')
  })
  const communityUnread = useQuery(api.community.getUnreadCount, {}) ?? 0

  // Derive admin mode from URL
  const isAdminMode = location.pathname.startsWith('/admin')

  // Close mobile menu and community panel on route change (mobile)
  useEffect(() => {
    setMobileMenuOpen(false)
    if (window.innerWidth < 768) {
      setCommunityOpen(false)
    }
  }, [location.pathname])

  // Track the actual visible viewport height. On iOS Safari, `100dvh` doesn't
  // shrink when the on-screen keyboard appears, which leaves the composer
  // stranded below the keyboard. We mirror visualViewport.height into a CSS
  // variable so the root container can resize and pin bottom-anchored UI
  // (composer, mobile tabs) just above the keyboard.
  useEffect(() => {
    const vv = window.visualViewport
    if (!vv) return
    const root = document.documentElement
    const update = () => {
      root.style.setProperty('--vvh', `${vv.height}px`)
    }
    update()
    vv.addEventListener('resize', update)
    vv.addEventListener('scroll', update)
    return () => {
      vv.removeEventListener('resize', update)
      vv.removeEventListener('scroll', update)
    }
  }, [])

  // Existing project routes auto-collapse the sidebar so the chat + preview
  // own the viewport. `/` (the new-video entry point) keeps the sidebar
  // expanded — the welcome composer is happy to share space with the nav.
  const isCreatePage = location.pathname.startsWith('/video/') || location.pathname.startsWith('/video2/') || location.pathname.startsWith('/project/') || location.pathname.startsWith('/admin/videos/view/') || location.pathname.startsWith('/admin/videos/replay/') || location.pathname.startsWith('/admin/projects/view/') || location.pathname.startsWith('/admin/projects/replay/')
  const collapseSidebar = isCreatePage
  // The "+ New video" nav item already gets you out of a project.
  const showBackButton = false

  const addDroppedAssets = useMutation(api.assets.addDroppedAssets)

  const handleFilesDropped = useCallback((files: MediaFile[]) => {
    if (files.length === 0) return
    const mapped = files.map((f) => {
      const parts = f.path.replace(/\\/g, '/').split('/')
      const folder = parts.slice(0, -1).join('/')
      return {
        filePath: f.path,
        fileName: f.name,
        folder,
        fileSize: f.size || undefined,
      }
    })
    addDroppedAssets({ files: mapped })
      .then(() => {
        track('media.files_dropped', { count: mapped.length, surface: 'desktop_drop' })
        toast({
          title: `Imported ${mapped.length} file${mapped.length !== 1 ? 's' : ''}`,
          description: 'Added to your assets',
        })
      })
      .catch((err) => {
        console.error('[DropZone] Failed to save files:', err)
        toast({
          title: 'Import failed',
          description: 'Could not add files to your assets',
          variant: 'destructive',
        })
      })
  }, [addDroppedAssets])

  useEffect(() => {
    const handler = () => setCommunityOpen(true)
    window.addEventListener('open-community-chat', handler)
    return () => window.removeEventListener('open-community-chat', handler)
  }, [])

  // Session.started: fire once per browser session for retention tracking
  useEffect(() => {
    if (!user || user.isAnonymous) return
    if (sessionStorage.getItem('footage_session_tracked')) return
    sessionStorage.setItem('footage_session_tracked', '1')

    const lastSession = parseInt(localStorage.getItem('footage_last_session') ?? '0', 10)
    const now = Date.now()
    const daysSince = lastSession ? Math.round((now - lastSession) / 86_400_000) : undefined
    const sessionCount = parseInt(localStorage.getItem('footage_session_count') ?? '0', 10) + 1
    localStorage.setItem('footage_last_session', String(now))
    localStorage.setItem('footage_session_count', String(sessionCount))

    track('session.started', { daysSinceLastSession: daysSince, sessionCount })
  }, [user])

  const classicNux = useFF('classic_desktop_nux')
  const communityChatEnabled = useFF('community_chat') === true

  // Anonymous visitors can browse the sidebar destinations directly. Every
  // other AppLayout route bounces home with the auth modal popped via #signin.
  // Page-level queries return empty for unauthed users, so /library and /assets
  // render harmless empty states.
  const ANON_ALLOWED = new Set(['/', '/library', '/assets', '/skills', '/support'])
  const isAnonAllowed = ANON_ALLOWED.has(location.pathname)

  // Suppress the loading spinner on anon-allowed routes. Otherwise the brief
  // `isAuthenticated && !profile` transition (e.g. right after lazy
  // `signIn('anonymous')` lands a JWT but before the profile query returns)
  // unmounts the route and wipes its local state — including the just-opened
  // AuthModal on `/`. Pages handle their own loading states.
  if (isLoading && !isAnonAllowed) {
    return (
      <div className="min-h-screen bg-card flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!isAuthenticated && !isAnonAllowed) {
    return <Navigate to="/#signin" replace />
  }

  // Suspended account — block all access except admin routes
  if (user?.isSuspended && !location.pathname.startsWith('/admin')) {
    return (
      <div className="min-h-screen bg-[#f5f5f7] flex items-center justify-center px-6">
        <div className="max-w-2xl text-center space-y-6">
          <p className="text-lg font-semibold text-red-500">
            Your account has been suspended for violating our terms of service.
          </p>
          <p className="text-base font-semibold text-red-500">
            Your subscription has been cancelled and any recent payments have been refunded.
            <br />
            You will see the money back in your account within the next 5-10 days.
          </p>
          <p className="text-base font-semibold text-red-500">
            Please contact {SUPPORT_EMAIL} if you have any other inquiries.
          </p>
        </div>
      </div>
    )
  }

  if (isElectron() && user && !user.nuxCompleted && classicNux === true) {
    return <Navigate to="/nux" replace />
  }

  // Admin route protection
  const isAdminRoute = location.pathname.startsWith('/admin')
  if (isAdminRoute && !user?.isAdmin) {
    return <Navigate to="/library" replace />
  }

  // Admin-only route protection (Templates, Trash, Uploads, Avatars, Voices, Feature Flags)
  const adminOnlyRoutes = ['/templates', '/trash', '/uploads', '/avatars', '/voices']
  if (adminOnlyRoutes.includes(location.pathname) && !user?.isAdmin) {
    return <Navigate to="/library" replace />
  }

  return (
    <UpdateGate>
    <AppModalsProvider>
    <div
      className="bg-background flex flex-col overflow-hidden"
      style={{ height: 'var(--vvh, 100dvh)' }}
    >
      <DropZone onFilesDropped={handleFilesDropped} />
      <DesktopAppBanner />
      <div className="flex-1 flex overflow-hidden">
      {/* Mobile Header */}
      <div className="fixed top-0 left-0 right-0 z-40 md:hidden">
        <header className="h-14 bg-background flex items-center justify-between px-4">
          <div className="flex items-center gap-3">
            {showBackButton ? (
              <Button
                variant="ghost"
                size="icon"
                asChild
                className="w-10 h-10 rounded-xl bg-neutral-800 text-white hover:bg-neutral-900 hover:text-white"
              >
                <Link to="/library">
                  <ArrowLeft className="h-5 w-5" />
                </Link>
              </Button>
            ) : (
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="p-2 rounded-lg hover:bg-muted"
              >
                <AlignLeft className="h-5 w-5 text-foreground" />
              </button>
            )}
          </div>
        </header>
      </div>

      {/* Mobile Menu Overlay - renders Sidebar as drawer */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setMobileMenuOpen(false)}
          />
          <div className="absolute left-0 top-0 bottom-0 w-64 h-full shadow-xl">
            <Sidebar
              isMobileDrawer
              onNavigate={() => setMobileMenuOpen(false)}
              isAdminMode={isAdminMode}
              communityUnread={communityUnread}
              onCommunityOpen={communityChatEnabled ? () => { setMobileMenuOpen(false); setCommunityOpen(true) } : undefined}
            />
          </div>
        </div>
      )}

      {/* Sidebar - hidden on mobile, always collapsed on create page */}
      <ErrorBoundary>
        <Sidebar
          collapsed={collapseSidebar}
          showBackButton={showBackButton}
          isAdminMode={isAdminMode}
          communityUnread={communityUnread}
          onCommunityOpen={communityChatEnabled ? () => setCommunityOpen(true) : undefined}
        />
      </ErrorBoundary>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden pt-14 md:pt-0 min-w-0">
        {isCreatePage || isAdminMode ? (
          <main className="flex-1 overflow-auto">
            <ErrorBoundary level="page" resetKey={location.pathname}>
              <Outlet />
            </ErrorBoundary>
          </main>
        ) : (
          <div className="flex-1 overflow-auto">
            <main className="flex-1 min-h-full">
              <ErrorBoundary level="page" resetKey={location.pathname}>
                <Outlet />
              </ErrorBoundary>
            </main>
          </div>
        )}
      </div>

      {/* Community chat panel */}
      <ErrorBoundary>
        <CommunityPanel open={communityOpen} onClose={() => setCommunityOpen(false)} />
      </ErrorBoundary>

      </div>

    </div>
    </AppModalsProvider>
    </UpdateGate>
  )
}
